import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@clarix/ui';
import { getApiErrorMessage } from '@clarix/utils';
import { masterConfigApi } from './master-config.api';
import { masterConfigKeys } from './config';
import type {
  CreateMasterCategoryPayload,
  CreateMasterValuePayload,
  UpdateMasterValuePayload,
} from './types';

export function useCategories() {
  return useQuery({
    queryKey: masterConfigKeys.categories,
    queryFn: () => masterConfigApi.listCategories(),
    staleTime: 10 * 60 * 1000,
  });
}

export function useValues(categoryCode: string) {
  return useQuery({
    queryKey: masterConfigKeys.values(categoryCode),
    queryFn: () => masterConfigApi.listValues(categoryCode),
    enabled: !!categoryCode,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMasterCategoryPayload) => masterConfigApi.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: masterConfigKeys.categories });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to create master'));
    },
  });
}

/**
 * Create a brand-new master category and seed it with values in one flow, with
 * a single toast + cache invalidation. Used by the object attribute builder
 * when an admin backs a dropdown/radio/multiselect field by a new master.
 *
 * Values are POSTed sequentially so their `sort_order` stays deterministic (the
 * backend has no transactional bulk-value endpoint yet); a mid-sequence failure
 * leaves a partial master, which the admin can finish editing in Settings.
 */
export function useCreateMasterWithValues() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      code: string;
      name: string;
      description?: string | null;
      values: { code: string; label: string }[];
    }) => {
      const category = await masterConfigApi.createCategory({
        code: input.code,
        name: input.name,
        description: input.description ?? null,
      });
      for (let i = 0; i < input.values.length; i += 1) {
        await masterConfigApi.createValue({
          category_code: input.code,
          code: input.values[i]!.code,
          label: input.values[i]!.label,
          sort_order: i,
        });
      }
      return category;
    },
    onSuccess: (category) => {
      toast.success('Master created successfully');
      queryClient.invalidateQueries({ queryKey: masterConfigKeys.categories });
      queryClient.invalidateQueries({ queryKey: masterConfigKeys.values(category.code) });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to create master'));
    },
  });
}

export function useCreateValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMasterValuePayload) => masterConfigApi.createValue(data),
    onSuccess: (_data, variables) => {
      toast.success('Value created successfully');
      queryClient.invalidateQueries({
        queryKey: masterConfigKeys.values(variables.category_code),
      });
      queryClient.invalidateQueries({
        queryKey: masterConfigKeys.categories,
      });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to create value'));
    },
  });
}

export function useUpdateValue(categoryCode: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMasterValuePayload }) =>
      masterConfigApi.updateValue(id, data),
    onSuccess: () => {
      toast.success('Value updated successfully');
      queryClient.invalidateQueries({
        queryKey: masterConfigKeys.values(categoryCode),
      });
      queryClient.invalidateQueries({
        queryKey: masterConfigKeys.categories,
      });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to update value'));
    },
  });
}

export function useDeleteValue(categoryCode: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => masterConfigApi.deleteValue(id),
    onSuccess: () => {
      toast.success('Value deleted successfully');
      queryClient.invalidateQueries({
        queryKey: masterConfigKeys.values(categoryCode),
      });
      queryClient.invalidateQueries({
        queryKey: masterConfigKeys.categories,
      });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to delete value'));
    },
  });
}

export function useReorderValues(categoryCode: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      masterConfigApi.reorderValues(categoryCode, orderedIds),
    onSuccess: () => {
      toast.success('Values reordered successfully');
      queryClient.invalidateQueries({
        queryKey: masterConfigKeys.values(categoryCode),
      });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to reorder values'));
    },
  });
}

export function useSeedDefaults() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => masterConfigApi.seedDefaults(),
    onSuccess: () => {
      toast.success('Default values seeded successfully');
      queryClient.invalidateQueries({
        queryKey: masterConfigKeys.all,
      });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to seed defaults'));
    },
  });
}

export function useApprovalReasons() {
  return useQuery({
    queryKey: masterConfigKeys.values('approval_reason'),
    queryFn: () => masterConfigApi.getApprovalReasons(),
    staleTime: 5 * 60 * 1000,
  });
}
